import { Button, Caption, InputGroup } from "@pej/kore/components";
import { Plus, Search } from "lucide-react";
import { type KeyboardEvent, useMemo, useRef, useState } from "react";
import { type CreateFolderResult, type FolderScopeValue, sameScope } from "./asset-types";

export interface FolderPickRow {
    /**
     * Identity, not display text. Two folders can be named alike across a
     * rename, and a list keyed on labels picks the wrong one the moment they
     * are. Reusing FolderScopeValue rather than a string key also means there
     * is no sentinel ("all", "unsorted") that a real folder id could collide
     * with.
     */
    value: FolderScopeValue;
    label: string;
}

export interface FolderPickListProps {
    rows: FolderPickRow[];
    selected: FolderScopeValue | null;
    /** Absent create affordance rather than a disabled one without manage. */
    canCreate: boolean;
    /** Section heading — a different register than the search label below. */
    title: string;
    /** The search box's accessible name / placeholder. */
    searchLabel: string;
    onChoose: (value: FolderScopeValue) => void;
    onCreate: (name: string) => Promise<CreateFolderResult>;
    onClose: () => void;
}

/**
 * Ranks matches by where the query lands in the label — an earlier hit outranks
 * a later one, ties broken alphabetically — rather than leaving results in
 * whatever order the caller supplied.
 */
function rankMatches(rows: FolderPickRow[], query: string): FolderPickRow[] {
    const q = query.toLowerCase();
    return rows
        .filter((row) => row.label.toLowerCase().includes(q))
        .sort((a, b) => {
            const byMatchPosition = a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q);
            return byMatchPosition !== 0 ? byMatchPosition : a.label.localeCompare(b.label);
        });
}

/**
 * A searchable list of folders with a create affordance at its foot. Mounted
 * three times with different row sets: the folder menu (spec §5.3), **Move
 * to…** (§5.7), and the upload target select (§5.4).
 *
 * Search is here because the hundred-folder cap requires it — a hundred rows is
 * an unusable bare list. Create is here because two of the three moments where
 * an operator wants a folder are moments where they are already looking at the
 * list and would otherwise have to leave, create, and come back.
 *
 * The component owns no error UI beyond the create field: a failed create
 * renders its message inline and keeps the list open, because the correction —
 * a different name — happens right here.
 */
export function FolderPickList({
    rows,
    selected,
    canCreate,
    title,
    searchLabel,
    onChoose,
    onCreate,
    onClose,
}: FolderPickListProps) {
    const [query, setQuery] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    // A ref as well as state: two rapid clicks are two synchronous event
    // dispatches, and the second can run before React has committed the first
    // `setState`. The ref reads the guard immediately.
    const creatingRef = useRef(false);

    const trimmed = query.trim();
    const visible = useMemo(() => (trimmed ? rankMatches(rows, trimmed) : rows), [rows, trimmed]);

    // Roving tabindex — the same APG pattern asset-tile-grid.tsx implements
    // for the same reason, cited in that file's own docblock: the hundred-
    // folder cap is exactly why this list needs the quick search above it,
    // so a hundred rows must not also become a hundred sequential tab stops
    // (I5, final review). Clamped at read time, not corrected via an effect,
    // so a search that shrinks `visible` out from under a later index can
    // never render a frame with zero roving tab stops.
    const [activeIndex, setActiveIndex] = useState(0);
    const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
    const activeRowIndex = visible.length === 0 ? -1 : Math.min(activeIndex, visible.length - 1);

    const moveTo = (index: number) => {
        setActiveIndex(index);
        rowRefs.current[index]?.focus();
    };

    const handleCreate = async () => {
        if (creatingRef.current || trimmed.length === 0) return;
        creatingRef.current = true;
        setCreating(true);
        setError(null);
        try {
            const result = await onCreate(trimmed);
            if (result.ok) setQuery("");
            else setError(result.message);
        } finally {
            creatingRef.current = false;
            setCreating(false);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") onClose();
    };

    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: catches Escape bubbling up from the search box (a sibling of the listbox, not a descendant) as well as from a row; it carries no interactive role of its own and needs none — everything a pointer or keyboard user actually acts on inside is its own focusable element.
        <div data-testid="folder-pick-list" className="flex w-64 flex-col gap-2" onKeyDown={handleKeyDown}>
            <p className="text-sm font-medium">{title}</p>
            <InputGroup>
                <InputGroup.Prefix>
                    <Search aria-hidden="true" className="size-3.5 text-kore-text-muted" />
                </InputGroup.Prefix>
                <InputGroup.Input
                    type="search"
                    aria-label={searchLabel}
                    placeholder={`${searchLabel}…`}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                />
            </InputGroup>

            {/* Caption's props type has no `role` slot to carry "alert" itself
                (spec's supplied `<Caption role="alert">` doesn't typecheck, no
                matter the `as`), so the region wraps it instead. */}
            {error && (
                <div role="alert">
                    <Caption className="px-1 text-kore-text-destructive">{error}</Caption>
                </div>
            )}

            {/* Its own label, distinct from the search Input's above: the two
                shared one name before this fix, which left assistive tech
                unable to tell "type here to search" from "these are the
                results" apart (deferred item 3, final review). */}
            <div
                role="listbox"
                aria-label={`${searchLabel} results`}
                className="flex max-h-64 flex-col gap-0.5 overflow-y-auto"
            >
                {/* Every mount pins at least one row (Unsorted, sometimes also
                    All — see the callers), so `visible` can only be empty once
                    a search has filtered everything out; there is no live case
                    where `rows` itself starts empty (C1, final review). If a
                    future caller changes that invariant, this needs a real
                    "no folders yet" branch back. */}
                {visible.length === 0 && (
                    <Caption className="px-2 py-3 text-kore-text-muted">No folders match "{trimmed}".</Caption>
                )}
                {visible.map((row, index) => (
                    <div
                        key={JSON.stringify(row.value)}
                        ref={(el) => {
                            rowRefs.current[index] = el;
                        }}
                        role="option"
                        aria-selected={selected !== null && sameScope(selected, row.value)}
                        aria-label={row.label}
                        tabIndex={index === activeRowIndex ? 0 : -1}
                        onFocus={() => setActiveIndex(index)}
                        onClick={() => onChoose(row.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                onChoose(row.value);
                                return;
                            }
                            if (event.key === "ArrowDown") {
                                event.preventDefault();
                                moveTo(Math.min(index + 1, visible.length - 1));
                            } else if (event.key === "ArrowUp") {
                                event.preventDefault();
                                moveTo(Math.max(index - 1, 0));
                            } else if (event.key === "Home") {
                                event.preventDefault();
                                moveTo(0);
                            } else if (event.key === "End") {
                                event.preventDefault();
                                moveTo(visible.length - 1);
                            }
                        }}
                        className="flex cursor-default items-center rounded-kore-control px-2 py-1.5 outline-none hover:bg-kore-surface-tint focus-visible:ring-2 focus-visible:ring-kore-accent aria-selected:bg-kore-accent/10"
                    >
                        <span className="truncate">{row.label}</span>
                    </div>
                ))}
            </div>

            {/* Creation lives at the foot (spec §5.4). The search box doubles as the new
                folder's name, so with nothing typed there is nothing to create. Rather than a
                disabled button — which reads as "you lack the permission" (the exact confusion
                spec §14 warns against) — the foot shows a hint, and the actionable button
                appears the moment a name is typed. Capability is still all-or-nothing: no
                `canCreate`, no foot at all. */}
            {canCreate &&
                (trimmed.length === 0 ? (
                    <Caption className="px-1 text-kore-text-muted">
                        Type a name in the search box to create a folder.
                    </Caption>
                ) : (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="justify-start"
                        disabled={creating}
                        onClick={handleCreate}
                    >
                        <Plus aria-hidden="true" />
                        {`Create "${trimmed}"`}
                    </Button>
                ))}
        </div>
    );
}
